import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ChevronsUpDown, Check } from "lucide-react";

/**
 * Searchable combobox for Clients or Carriers.
 * Never allows free text — user must pick from catalog or create a new record inline.
 */
export function EntityAutocomplete({ type, value, label, onSelect, paisFilter, placeholder = "Buscar código o nombre...", testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEntity, setNewEntity] = useState({ razon_social: "", pais: paisFilter || (type === "clients" ? "US" : "MX") });
  const boxRef = useRef(null);
  const entityLabel = type === "clients" ? "cliente" : "carrier";

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { search: query, activo: "true", limit: 8 };
        if (paisFilter) params.pais = paisFilter;
        const { data } = await api.get(`/${type}`, { params });
        setResults(data.items);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, type, paisFilter]);

  const select = (entity) => {
    onSelect(entity);
    setOpen(false);
    setQuery("");
  };

  const createEntity = async () => {
    if (!newEntity.razon_social.trim()) return;
    setCreating(true);
    try {
      const payload = type === "clients"
        ? { razon_social: newEntity.razon_social, pais: newEntity.pais, terminos_pago_dias: 30 }
        : { razon_social: newEntity.razon_social, pais: newEntity.pais, moneda_default: newEntity.pais === "MX" ? "MXN" : "USD" };
      const { data } = await api.post(`/${type}?force=true`, payload);
      toast.success(type === "clients" ? "Cliente creado" : "Carrier creado");
      select(data);
      setShowCreate(false);
      setNewEntity({ razon_social: "", pais: paisFilter || (type === "clients" ? "US" : "MX") });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className="flex w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm"
      >
        <span className={`truncate ${label ? "text-foreground" : "text-muted-foreground"}`}>{label || placeholder}</span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-white shadow-lg">
          <div className="p-2">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Escribe código o nombre..."
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {loading && <div className="px-3 py-2 text-xs text-muted-foreground">Buscando...</div>}
            {!loading && results.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Sin resultados.</div>
            )}
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => select(r)}
                data-testid={`${testId}-option-${r.codigo}`}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
              >
                {value === r.id && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                <span className="font-mono text-xs text-muted-foreground">{r.codigo}</span>
                <span className="truncate">{r.razon_social}</span>
              </button>
            ))}
          </div>
          <div className="border-t border-border p-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-full gap-2"
              onClick={() => { setShowCreate(true); setOpen(false); }}
              data-testid={`${testId}-create-btn`}
            >
              <Plus className="h-3.5 w-3.5" /> Crear {entityLabel} nuevo
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Crear {entityLabel} nuevo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Razón social *</Label>
              <Input
                className="mt-1"
                value={newEntity.razon_social}
                onChange={(e) => setNewEntity({ ...newEntity, razon_social: e.target.value })}
                data-testid="entity-create-razon-social"
              />
            </div>
            {!paisFilter && (
              <div>
                <Label className="text-xs text-muted-foreground">País</Label>
                <Select value={newEntity.pais} onValueChange={(v) => setNewEntity({ ...newEntity, pais: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">US</SelectItem>
                    <SelectItem value="MX">MX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={createEntity} disabled={creating || !newEntity.razon_social.trim()} data-testid="entity-create-save">
              {creating ? "Creando..." : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
